import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { usePageAccess } from '@/hooks/usePageAccess'

const tabs = [
  { to: '/hp-threshold/balances', label: 'Balances' },
  { to: '/hp-threshold/settings', label: 'Settings', requiresEdit: true },
  { to: '/hp-threshold/conventions', label: 'Conventions' },
] as const

export default function HpThresholdLayout() {
  const { canEdit } = usePageAccess('hp-threshold')
  const location = useLocation()

  const visibleTabs = tabs.filter((tab) => !('requiresEdit' in tab && tab.requiresEdit) || canEdit)

  if (!canEdit && location.pathname.startsWith('/hp-threshold/settings')) {
    return <Navigate to="/hp-threshold/balances" replace />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <h1 className="hidden text-xl font-semibold text-gray-900 lg:block">HP Threshold</h1>
          <p className="mt-0.5 text-sm text-gray-500 lg:mt-0.5 max-lg:mt-0">
            Unreturned HP Threshold balances and settings
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 lg:px-6 py-4">
          <div className="relative -mx-4 px-4 lg:mx-0 lg:px-0">
            <div className="overflow-x-auto snap-x snap-mandatory overscroll-x-contain lg:snap-none">
              <div
                className="inline-flex gap-2 bg-gray-200 p-1 rounded-lg min-w-max"
                role="tablist"
                aria-label="HP Threshold sections"
              >
                {visibleTabs.map((tab) => (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    role="tab"
                    className={({ isActive }) =>
                      `snap-start px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-white text-brand-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`
                    }
                  >
                    {tab.label}
                  </NavLink>
                ))}
              </div>
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent lg:hidden"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent lg:hidden"
              aria-hidden
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <Outlet />
      </div>
    </div>
  )
}
